// The Pagelet runtime loader (the one tag). Classic IIFE: boots core, detects which
// capabilities the page uses, and lazy-imports only those module bundles at runtime.
// Online-to-render (D14): the offline banner shows until this boots successfully.
import { BASE_CSS } from './styles'
import { initMode, applyTheme, injectThemeCompatibility, setMode } from './theme'
import { mountToolbar } from './toolbar'
import { mountChrome } from './toc'
import { initVersions } from './versions'
import { collectMarkdown } from './export'
import { warn } from './util'
import type { InteractiveApi } from './modules/interactive'

const VERSION = '0.2.6'
const STYLE_ID = 'pagelet-base-css'

// Capture the loader's own base URL synchronously (currentScript is null inside later callbacks).
const BASE = resolveBase()

interface PageletApi {
  version: string
  base: string
  exportMarkdown(): string
  setTheme(mode: 'light' | 'dark' | 'auto'): void
  /** Present once the lazily-loaded `interactive` module has booted (see its `pagelet:interactive-ready` event). */
  interactive?: InteractiveApi
}
declare global {
  interface Window {
    __pagelet__?: PageletApi
  }
}

function resolveBase(): string {
  // Immutable-release builds are ESM: document.currentScript is null inside modules, so resolve
  // from the module's own URL (the import never comes from the original /v1 script attribute).
  // The IIFE build defines import.meta.url away, so this branch is skipped there.
  try {
    const own = import.meta.url
    if (own) return new URL('.', own).href
  } catch {
    // IIFE/classic path below.
  }
  const src = (document.currentScript as HTMLScriptElement | null)?.src
  try {
    return new URL('.', src || location.href).href
  } catch {
    return './'
  }
}

/** Lazy-import a capability module by name; runtime URL keeps esbuild from bundling it. */
function load(name: string): Promise<Record<string, (...a: never[]) => unknown>> {
  const url = new URL(`modules/${name}.js`, BASE).href
  return import(/* @vite-ignore */ url) as Promise<Record<string, (...a: never[]) => unknown>>
}

function injectBaseCss(): void {
  if (document.getElementById(STYLE_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = BASE_CSS
  // Runtime tokens are defaults, not authority. Put them first in the cascade so a pagelet's
  // own :root/[data-theme] variables and component styles keep winning after the runtime boots.
  document.head.insertBefore(style, document.head.firstChild)
}

function hasMath(scope: Element): boolean {
  const t = scope.textContent || ''
  return t.includes('$$') || t.includes('\\(') || t.includes('\\[')
}

async function run(name: string): Promise<void> {
  try {
    const mod = await load(name)
    await (mod.enhance as () => Promise<void>)()
  } catch (e) {
    warn(`${name}-load`, e)
  }
}

async function boot(): Promise<void> {
  if (window.__pagelet__) return
  window.__pagelet__ = {
    version: VERSION,
    base: BASE,
    exportMarkdown: collectMarkdown,
    setTheme: setMode,
  }

  injectBaseCss()
  injectThemeCompatibility()
  initMode()
  try {
    applyTheme()
  } catch (e) {
    warn('theme', e)
  }

  // 1. Markdown first (produces .pagelet-prose; normalises ```mermaid blocks).
  if (document.querySelector('script[type="text/markdown"][data-pagelet="markdown"]')) {
    await run('markdown')
  }

  // 2. Detect what the (now-rendered) page uses; load those modules in parallel.
  const scope = (document.querySelector('.pagelet-prose') as Element) || document.body
  const jobs: Promise<void>[] = []
  if (document.querySelector('pre.mermaid, .mermaid')) jobs.push(run('mermaid'))
  if (document.querySelector('pre > code[class*="language-"]:not(.language-mermaid)')) jobs.push(run('code'))
  if (hasMath(scope)) jobs.push(run('math'))
  if (document.querySelector('script[type="application/json"][data-pagelet="chart"]')) jobs.push(run('charts'))
  if (document.querySelector('script[type="application/json"][data-pagelet="dataset"]')) jobs.push(run('tables'))
  if (document.querySelector('[data-pagelet-interactive], body.pagelet-interactive')) jobs.push(run('interactive'))
  await Promise.all(jobs)

  // 3. Chrome + toolbar (PNG export is itself lazy-loaded).
  mountChrome()
  mountToolbar(() => {
    void load('png').then((m) => (m.exportPng as () => Promise<void>)())
  })

  // 4. Version awareness (refresh toast + timeline) — same-origin, fails silent off-platform.
  //    After the toolbar so the history button has somewhere to mount; never blocks readiness.
  void initVersions()

  document.documentElement.classList.add('pagelet-ready')
  document.getElementById('pagelet-offline')?.remove()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void boot())
} else {
  void boot()
}
